/**
 * domain/motions.ts
 * The motion taxonomy — the engine's recommendation families. Shared by the
 * Rules list, Builder, Proposals, Performance and Settings so motion labels,
 * families and (monochrome) tones are defined exactly once.
 */
import { Tone } from '../theme/colors';

export type MotionKey =
  | 'tier_up'
  | 'size_up'
  | 'premium_variant'
  | 'complement'
  | 'bundle_fill'
  | 'accessory'
  | 'service_attach'
  | 'protection'
  | 'volume'
  | 'threshold_nudge'
  | 'next_best_action'
  | 'downsell';

export type FamilyKey = 'Upsell' | 'Cross-sell' | 'Service' | 'Nudge' | 'Down-sell';

export interface MotionMeta {
  label: string;
  family: FamilyKey;
  tone: Tone;
}

export const MOTIONS: Record<MotionKey, MotionMeta> = {
  tier_up: { label: 'Upgrade', family: 'Upsell', tone: 'accent' },
  size_up: { label: 'Bigger size', family: 'Upsell', tone: 'accent' },
  premium_variant: { label: 'Premium pick', family: 'Upsell', tone: 'accent' },
  complement: { label: 'Pairs well', family: 'Cross-sell', tone: 'success' },
  bundle_fill: { label: 'Complete the order', family: 'Cross-sell', tone: 'success' },
  accessory: { label: 'Add-on', family: 'Cross-sell', tone: 'success' },
  service_attach: { label: 'Add a service', family: 'Service', tone: 'warn' },
  protection: { label: 'Add protection', family: 'Service', tone: 'warn' },
  volume: { label: 'Buy more', family: 'Nudge', tone: 'info' },
  threshold_nudge: { label: 'Spend a bit more', family: 'Nudge', tone: 'info' },
  next_best_action: { label: 'Smart suggestion', family: 'Nudge', tone: 'purple' },
  downsell: { label: 'Budget option', family: 'Down-sell', tone: 'neutral' },
};

export interface FamilyFilter {
  key: 'all' | FamilyKey;
  label: string;
  /** Dot tone for the filter chip; 'all' has no dot tint. */
  tone: Tone | null;
}

export const FAMILIES: FamilyFilter[] = [
  { key: 'all', label: 'All', tone: null },
  { key: 'Upsell', label: 'Upsell', tone: 'accent' },
  { key: 'Cross-sell', label: 'Cross-sell', tone: 'success' },
  { key: 'Service', label: 'Service', tone: 'warn' },
  { key: 'Nudge', label: 'Spend more', tone: 'info' },
  { key: 'Down-sell', label: 'Down-sell', tone: 'neutral' },
];

/** Safe lookup — unknown motions fall back to a neutral chip. */
export function motionMeta(key: string): MotionMeta {
  return MOTIONS[key as MotionKey] ?? { label: key, family: 'Nudge', tone: 'neutral' };
}

/** Builder motion <select> options. */
export const MOTION_OPTIONS = (Object.keys(MOTIONS) as MotionKey[]).map((k) => ({
  value: k,
  label: MOTIONS[k].label,
}));

/** Pick strategies (the builder's "picked by" select; gating comes from /capabilities). */
export interface StrategyMeta {
  value: string;
  label: string;
}

export const STRATEGY_LABELS: Record<string, string> = {
  curated_order: 'your curated order',
  attach_rate: 'most-accepted (attach-rate)',
  most_popular: 'most popular',
  price_up_nearest: 'nearest price-up',
  expected_value: 'expected value',
  highest_margin: 'highest margin',
  similarity_score: 'most similar',
};

/** Short strategy labels for preset/manifest rows. */
export const STRATEGY_SHORT: Record<string, string> = {
  curated_order: 'curated order',
  attach_rate: 'most-accepted',
  most_popular: 'most popular',
  price_up_nearest: 'nearest price-up',
  expected_value: 'expected value',
  highest_margin: 'highest margin',
  similarity_score: 'most similar',
};
